export const createAudio = () => {
    const audio = new Audio();
    audio.volume = 0.6;
    audio.crossOrigin = 'anonymous';
    return audio;
};

export const downSampleChunk = (
    chunk: Float32Array,
    inputSampleRate: number = 48000,
    outputSampleRate: number = 16000,
) => {
    if (outputSampleRate >= inputSampleRate) {
        throw new Error('Output sample rate must be lower than input sample rate.');
    }
    const sampleRateRatio = inputSampleRate / outputSampleRate;
    const newLength = Math.floor(chunk.length / sampleRateRatio);
    const result = new Float32Array(newLength);

    for (let i = 0; i < newLength; i++) {
        const start = Math.floor(i * sampleRateRatio);
        const end = Math.floor((i + 1) * sampleRateRatio);
        let sum = 0;
        let count = 0;
        for (let j = start; j < end && j < chunk.length; j++) {
            sum += chunk[j];
            count++;
        }
        result[i] = count > 0 ? sum / count : 0;
    }
    return result;
};

export const encodeToWav = (samples: Float32Array, sampleRate: number = 16000) => {
    const numChannels = 1; // mono
    const bytesPerSample = 2;
    const blockAlign = numChannels * bytesPerSample;
    const bufferLength = 44 + samples.length * bytesPerSample;
    const buffer = new ArrayBuffer(bufferLength);
    const view = new DataView(buffer);

    // Write WAV header.
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * bytesPerSample, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // PCM header size
    view.setUint16(20, 1, true); // Audio format (PCM)
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);
    writeString(view, 36, 'data');
    view.setUint32(40, samples.length * bytesPerSample, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i++, offset += 2) {
        let s = Math.max(-1, Math.min(1, samples[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }

    return new Blob([view], { type: 'audio/wav' });
};

export const mergeChunks = (chunks: Float32Array[]) => {
    const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const result = new Float32Array(totalLength);
    let offset = 0;
    chunks.forEach((chunk) => {
        result.set(chunk, offset);
        offset += chunk.length;
    });
    return result;
};

function writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
    }
}

export const float32To16BitPCMBuffer = (float32Array: Float32Array) => {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
        let s = Math.max(-1, Math.min(1, float32Array[i]));
        int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return int16Array.buffer;
};
